<template>
  <div class="renew-user">
    <div class="renew-user--box">
      <div class="renew-user--header">
        <img class="renew-user--logo" src="@/assets/images/letsbook-icon.svg" />
      </div>
      <div class="renew-user--body">
        <!-- LOADING STATE -->
        <div v-if="isLoading" class="loading-message">
          <div class="loading-spinner"></div>
          <p>Validando sua conta...</p>
        </div>

        <!-- SUCCESS/ERROR STATE -->
        <div v-else class="result-message">
          <h2 class="result-title">
            {{
              validationSuccess
                ? 'Sua conta foi validada com sucesso!'
                : isLinkExpired
                ? 'Link expirado'
                : 'Ocorreu um erro'
            }}
          </h2>
          <p class="result-subtitle">
            <template v-if="validationSuccess">
              Seu acesso ao Letsbook foi estendido por mais 3 meses.<br />
              Clique no botão abaixo para fazer login e continuar.
            </template>
            <template v-else-if="isLinkExpired">
              Este link de validação expirou ou já foi utilizado.<br />
              Solicite um novo link de reativação.
            </template>
            <template v-else>
              Não foi possível validar sua conta.<br />
              Tente novamente mais tarde ou entre em contato com o suporte.
            </template>
          </p>
          <div class="button-container">
            <PmwButton
              size="fluid"
              variation="primary-120"
              @click="handleLogin"
            >
              {{ validationSuccess ? 'Fazer Login' : 'Ir para Login' }}
            </PmwButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PmwButton from '@solucoes/components-lib-tmp/src/components/elements/PmwButton'

export default {
  name: 'RenewUserLayout',
  components: {
    PmwButton
  },
  data() {
    return {
      isLoading: true,
      validationSuccess: false,
      errorMessage: '',
      isLinkExpired: false
    }
  },
  async mounted() {
    await this.validateUser()
  },
  methods: {
    /** Valida o usuário através da API de reativação */
    async validateUser() {
      try {
        /** Pegar o uid e dtexp dos query parameters */
        const uid = this.$route.query.uid
        const dtexp = this.$route.query.dtexp

        if (!uid || !dtexp) {
          this.validationSuccess = false
          this.errorMessage = 'Link inválido ou incompleto'
          return
        }

        /** Verificar se o link expirou */
        const expirationDate = new Date(dtexp)
        if (isNaN(expirationDate.getTime()) || new Date() > expirationDate) {
          this.isLinkExpired = true
          return
        }

        /** Fazer a chamada para a API */
        const response = await this.$auth.reactivateUser({ uid, dtexp })

        if (response.data.sucesso) {
          this.validationSuccess = true
        } else if (response.data.dataExpiracao === null) {
          this.isLinkExpired = true
        } else {
          throw new Error(response.data.erros || 'Erro na validação')
        }
      } catch (error) {
        if (
          error.response?.status === 400 &&
          error.response?.data?.dataExpiracao === null
        ) {
          this.isLinkExpired = true
        } else {
          this.validationSuccess = false
          this.errorMessage =
            error.response?.data?.erros || error.message || 'Erro desconhecido'
        }
      } finally {
        this.isLoading = false
      }
    },

    /** Redireciona para a página de login */
    handleLogin() {
      this.$router.push('/login')
    }
  }
}
</script>

<style lang="scss" scoped>
.renew-user {
  display: flex;
  align-items: center;
  width: 100%;
  height: 100vh;
  max-height: 100%;
  overflow: auto;
  background: pmw-colors('grey-10');
  place-content: center;

  @media screen and (max-width: 1000px) {
    padding: 0px;
    justify-content: center;
  }

  &--box {
    background: $white;
    width: 500px;
    height: 330px;
    z-index: 1;
    border-radius: 8px;
    @include box-shadow(4px 4px 15px rgba(0, 0, 0, 0.1));
  }

  &--header {
    border-radius: 8px 8px 0 0;
    height: 82px;
    background: pmw-colors('primary-100');
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &--logo {
    width: 130px;
    height: 42px;
    object-fit: contain;
  }

  &--body {
    padding: 40px 60px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 248px;
    box-sizing: border-box;

    .loading-message,
    .result-message {
      text-align: center;

      .result-title {
        color: pmw-colors('grey-90');
        font-size: pmw-font-size('paragraph-100');
        font-weight: pmw-font-weight('weight-semi-bold');
        margin: 0 0 20px 0;
        line-height: 1.3;
      }

      .result-subtitle {
        color: pmw-colors('grey-80');
        font-size: pmw-font-size('size-large');
        font-weight: pmw-font-weight('weight-normal');
        line-height: 1.5;
        margin: 0 0 32px 0;
      }

      .button-container {
        display: flex;
        justify-self: center;

        ::v-deep(.pmw-button) {
          width: 178px !important;
          height: 40px !important;
          font-size: pmw-font-size('size-large') !important;
        }
      }
    }

    .loading-message {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;

      .loading-spinner {
        width: 32px;
        height: 32px;
        border: 3px solid pmw-colors('grey-10');
        border-top: 3px solid pmw-colors('primary-100');
        border-radius: 50%;
        margin: 0 auto 20px auto;
        animation: spin 1s linear infinite;
      }

      p {
        color: pmw-colors('grey-70');
        font-size: pmw-font-size('paragraph-80');
        font-weight: pmw-font-weight('weight-normal');
        margin: 0;
      }
    }
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
}
</style>
